/**
 * Small additions to Horizon's header (TROOP-858).
 *
 * Reserved header height: keeps the space of the full header while the slim sticky header
 * shows. Without it, the height change of the slim header shifts the page. Scroll anchoring then
 * moves the scroll position, which header.js reports as a change of scroll direction, so the
 * header flickers. The CSS in snippets/armypoint-header.liquid uses the measured height.
 *
 * Search combobox: the results dropdown of snippets/armypoint-header-search.liquid is shown by
 * CSS (focus inside and a search term); this mirrors that state into aria-expanded.
 */

/** Breakpoints where the full header changes height (logo size and layout) */
const HEADER_BREAKPOINTS = ['(min-width: 750px)', '(min-width: 1300px)'];

/** @type {AbortController | null} */
let controller = null;

/** @type {ResizeObserver | null} */
let resizeObserver = null;

function init() {
  controller?.abort();
  resizeObserver?.disconnect();
  controller = new AbortController();
  const { signal } = controller;

  reserveHeaderHeight(signal);
  syncSearchExpanded(signal);
}

/**
 * @param {AbortSignal} signal
 */
function reserveHeaderHeight(signal) {
  const header = document.querySelector('#header-component');
  const section = header?.closest('.header-section');
  if (!(header instanceof HTMLElement) || !(section instanceof HTMLElement)) return;

  resizeObserver = new ResizeObserver(([entry]) => {
    // Only the full header counts; the slim one must not overwrite the reserved height
    if (!entry?.borderBoxSize[0] || header.dataset.stickyState === 'active') return;

    section.style.setProperty('--ap-header-full-height', `${Math.round(entry.borderBoxSize[0].blockSize)}px`);
  });
  resizeObserver.observe(header);

  // The full height of the other layout is unknown while slim; drop the stale value until the
  // full header shows again and gets measured
  for (const query of HEADER_BREAKPOINTS) {
    window
      .matchMedia(query)
      .addEventListener('change', () => section.style.removeProperty('--ap-header-full-height'), { signal });
  }
}

/**
 * @param {AbortSignal} signal
 */
function syncSearchExpanded(signal) {
  for (const search of document.querySelectorAll('.ap-header-search')) {
    const input = search.querySelector('input[role="combobox"]');
    if (!(input instanceof HTMLInputElement)) continue;

    const update = () => {
      const expanded = search.matches(':focus-within') && input.value.trim() !== '';
      input.setAttribute('aria-expanded', String(expanded));
    };

    // keyup covers Escape, which clears the field without an input event
    for (const type of ['focusin', 'focusout', 'input', 'keyup']) {
      search.addEventListener(type, () => requestAnimationFrame(update), { signal });
    }
  }
}

init();
document.addEventListener('shopify:section:load', init);
